import React, { useState } from "react";
import { useSelector } from "react-redux";
import { ChevronDown, ChevronsDownUp, ChevronsUpDown, Loader2, LogOut, Plus } from "lucide-react";
import Countdown from "../bond-wheel/Countdown";
import { selectBondWheelParticipation } from "../../redux/bondWheelSlice";
import { formatCoin, getGameMinimumPlayers, getGameNumberOfWinners, getGameWinnerEntries, getGameWinnerName } from "../../utils/bondWheelHelpers";
import { formatAppTime } from "../../utils/appTimeFormat";

const BondWheelGameRow = ({
  game,
  active,
  expanded,
  onSelect,
  onToggleExpand,
  onLeave,
  leaving,
  userId,
}) => {
  const participation = useSelector((state) =>
    selectBondWheelParticipation(state, game._id, userId)
  );

  const isLocked = game.status === "Locked";
  const isSettled = game.status === "Settled";
  const isCancelled = game.status === "Cancelled";
  const isPast = isSettled || isCancelled;
  const isLive = game.status === "Active" || isLocked;
  const playerCount =
    game.entries?.filter((e) => ["Active", "Won", "Lost"].includes(e.status))?.length || 0;
  const minimumPlayers = getGameMinimumPlayers(game);
  const numberOfWinners = getGameNumberOfWinners(game);
  const winnerEntries = isSettled ? getGameWinnerEntries(game) : [];
  const winnerName = isSettled ? getGameWinnerName(game) : null;

  let statusClass = "live";
  let statusLabel = "Live";
  if (isLocked) {
    statusClass = "locked";
    statusLabel = "Locked";
  } else if (isSettled) {
    statusClass = "settled";
    statusLabel = "Done";
  } else if (isCancelled) {
    statusClass = "cancelled";
    statusLabel = "Canceled";
  }

  const lockLabel = game.lockAt
    ? formatAppTime(game.lockAt)
    : null;

  return (
    <div className={`bplay-game-item ${active ? "active" : ""} ${expanded ? "expanded" : ""} ${isPast ? "past" : ""}`}>
      <div className="bplay-game-row-shell">
        <button type="button" className="bplay-game-row" onClick={() => onSelect?.(game)}>
          <div className="bplay-game-row-top">
            <span className="bplay-game-row-host">{game.creator?.name || "Host"}</span>
            <span className={`bplay-game-row-status ${statusClass}`}>{statusLabel}</span>
          </div>
          <div className="bplay-game-row-pot">{formatCoin(game.totalPot)} COIN</div>
          <div className="bplay-game-row-meta">
            <span>
              {playerCount} joined · min {minimumPlayers} · {numberOfWinners} win{numberOfWinners === 1 ? "" : "s"}
            </span>
            {isPast ? (
              <span className="bplay-game-row-result">
                {isCancelled
                  ? "Refunded"
                  : winnerEntries.length > 1
                  ? `${winnerEntries.length} winners`
                  : winnerName
                  ? `${winnerName.split(" ")[0]} won`
                  : "Settled"}
              </span>
            ) : (
              <Countdown deadline={game.deadline} tone={isLocked ? "lock" : "live"} />
            )}
          </div>
        </button>

        <button
          type="button"
          className="bplay-game-expand"
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse wheel details" : "Expand wheel details"}
          onClick={() => onToggleExpand?.(game)}
        >
          <ChevronDown size={14} />
        </button>
      </div>

      {expanded ? (
        <div className="bplay-game-row-detail">
          <div className="bplay-game-row-stats">
            <div>
              <span>Entry</span>
              <strong>{formatCoin(game.entryAmount)} COIN</strong>
            </div>
            <div>
              <span>Joined</span>
              <strong>{playerCount}</strong>
            </div>
            <div>
              <span>Min</span>
              <strong>{minimumPlayers}</strong>
            </div>
            <div>
              <span>Winners</span>
              <strong>{numberOfWinners}</strong>
            </div>
            {lockLabel ? (
              <div>
                <span>Locks</span>
                <strong>{lockLabel}</strong>
              </div>
            ) : null}
          </div>

          {participation.isCreator ? (
            <p className="bplay-game-row-note">You are hosting this wheel.</p>
          ) : null}

          {isLive && participation.isParticipant && participation.canLeave ? (
            <button
              type="button"
              className="bplay-game-row-leave"
              disabled={leaving}
              onClick={() => onLeave?.(game)}
            >
              {leaving ? <Loader2 size={14} className="bplay-spin" /> : <LogOut size={14} />}
              <span>{leaving ? "Leaving…" : "Leave wheel"}</span>
            </button>
          ) : null}

          {isLive && participation.isParticipant && !participation.canLeave && !participation.isCreator ? (
            <p className="bplay-game-row-note muted">Join window closed — you cannot leave now.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const BondWheelGameRail = ({
  games = [],
  selectedGameId,
  onSelectGame,
  onLeave,
  onCreate,
  loading = false,
  leavingGameId = null,
  userId,
}) => {
  const [listExpanded, setListExpanded] = useState(false);
  const [expandedGameId, setExpandedGameId] = useState(null);

  const liveGames = games.filter((g) => g.status === "Active" || g.status === "Locked");
  const pastGames = games.filter((g) => g.status === "Settled" || g.status === "Cancelled");

  const handleSelect = (game) => {
    onSelectGame?.(game);
    setExpandedGameId(String(game._id));
  };

  const handleToggleExpand = (game) => {
    const id = String(game._id);
    setExpandedGameId((prev) => (prev === id ? null : id));
    if (expandedGameId !== id) onSelectGame?.(game);
  };

  const renderItem = (game) => (
    <BondWheelGameRow
      key={String(game._id)}
      game={game}
      userId={userId}
      active={String(game._id) === String(selectedGameId)}
      expanded={String(game._id) === String(expandedGameId)}
      onSelect={handleSelect}
      onToggleExpand={handleToggleExpand}
      onLeave={onLeave}
      leaving={String(leavingGameId) === String(game._id)}
    />
  );

  return (
    <aside
      className={`bplay-games-rail ${listExpanded ? "is-expanded" : "is-collapsed"}`}
      aria-label="Bond wheels"
    >
      <div className="bplay-games-rail-head">
        <span className="bplay-games-rail-label">Wheels</span>
        <div className="bplay-games-head-actions">
          <button
            type="button"
            className="bplay-games-expand"
            onClick={() => setListExpanded((v) => !v)}
            title={listExpanded ? "Collapse list" : "Expand list"}
            aria-expanded={listExpanded}
          >
            {listExpanded ? <ChevronsDownUp size={14} /> : <ChevronsUpDown size={14} />}
          </button>
          <button type="button" className="bplay-games-create" onClick={onCreate} title="Create wheel">
            <Plus size={14} />
          </button>
        </div>
      </div>

      <div className="bplay-games-list">
        {loading && games.length === 0 ? (
          <p className="bplay-games-empty">Loading…</p>
        ) : games.length === 0 ? (
          <p className="bplay-games-empty">No wheels yet.</p>
        ) : (
          <>
            {liveGames.length > 0 ? (
              <>
                <p className="bplay-games-section-label">Live</p>
                {liveGames.map(renderItem)}
              </>
            ) : null}
            {pastGames.length > 0 ? (
              <>
                <p className="bplay-games-section-label">Past</p>
                {pastGames.map(renderItem)}
              </>
            ) : null}
          </>
        )}
      </div>
    </aside>
  );
};

export default BondWheelGameRail;
